import { inject, Injectable, signal } from '@angular/core';
import {
  OrderItemDetailsResponseDto,
  OrderItemRowTypeEnumResponseDto,
  OrderItemStatusEnumResponseDto,
  GetOrderItemDetailsResponseDto,
  OrderStatusGroupEnumResponseDto,
  TicketTypeEnumResponseDto,
} from '@core/services/apiclient';
import { TranslateService } from '@ngx-translate/core';
import { ChipItem } from '@shared/components';
import { all, include } from 'underscore';
import { OrderStatusGroupEnum, TreeListRowData } from '../models/orders.model';
import { OrdersService } from './orders.service';

@Injectable({
  providedIn: 'root',
})
export class OrdersStatusService {
  private readonly translate = inject(TranslateService);
  private readonly ordersService = inject(OrdersService);

  productPrintableCache = new Map<number, boolean>();
  private productPrintableLoading = new Set<number>();
  readonly productPrintableVersion = signal(0);

  private getSubscriptionGroupNonCanceledTotalCount(rowData: TreeListRowData): number {
    const totalCount = rowData?.orderItemCount ?? 0;
    const canceledCount = rowData?.canceledOrderRowsCount ?? 0;
    return Math.max(0, totalCount - canceledCount);
  }

  loadProductPrintableOnDemand(productId: number): void {
    if (this.productPrintableCache.has(productId) || this.productPrintableLoading.has(productId)) return;
    this.productPrintableLoading.add(productId);
    this.ordersService.getPrintableProduct(productId).subscribe({
      next: (value) => {
        this.productPrintableCache.set(productId, value?.isProductPrintable ?? false);
        this.productPrintableLoading.delete(productId);
        this.productPrintableVersion.update((v) => v + 1);
      },
      error: () => {
        this.productPrintableCache.set(productId, false);
        this.productPrintableLoading.delete(productId);
        this.productPrintableVersion.update((v) => v + 1);
      },
    });
  }

  isProductPrintable(product: TreeListRowData): boolean | null {
    if (product.statusGroup === OrderItemStatusEnumResponseDto.Paid) {
      const productId = product?.id;
      if (!this.productPrintableCache.has(productId)) {
        this.loadProductPrintableOnDemand(productId);
        return null;
      }
      return this.productPrintableCache.get(productId);
    }
  }

  getStatusChipData(rowData: TreeListRowData): ChipItem[] {
    if (this.isSubscriptionGroupWithPartialStatus(rowData)) {
      return this.getSubscriptionGroupChipData(rowData);
    }
    return this.getChipByStatus(rowData?.statusGroup, null);
  }

  private isSubscriptionGroupWithPartialStatus(rowData: TreeListRowData): boolean {
    return (
      rowData?.orderItemRowType === OrderItemRowTypeEnumResponseDto.SubscriptionGroup &&
      rowData?.statusCount != null &&
      rowData?.orderItemCount != null &&
      rowData.statusCount !== rowData.orderItemCount
    );
  }

  private getSubscriptionGroupChipData(rowData: TreeListRowData): ChipItem[] {
    const baseChips = this.getChipByStatus(rowData?.statusGroup, null);
    if (!baseChips?.length) return baseChips;

    const chip = baseChips[0];
    const statusLabel = this.translate.instant(chip.label);
    const baseChip: ChipItem[] = [{ ...chip, label: statusLabel }];

    if (rowData?.statusGroup === OrderItemStatusEnumResponseDto.Canceled) {
      return baseChip;
    }

    const totalCount = this.getSubscriptionGroupNonCanceledTotalCount(rowData);
    if (totalCount === 0) {
      return baseChip;
    }

    const statusCount = Math.min(rowData?.statusCount ?? 0, totalCount);
    if (statusCount === totalCount) {
      return baseChip;
    }

    return [{ ...chip, label: `${statusLabel} ${statusCount}/${totalCount}` }];
  }

  getChipByStatus(status: OrderItemStatusEnumResponseDto, ticketType: TicketTypeEnumResponseDto): ChipItem[] {
    switch (status) {
      case OrderItemStatusEnumResponseDto.Edited:
        return [
          {
            textColor: 'var(--color-black)',
            color: 'var(--color-success-100)',
            icon: this.getPrintingIconByTicketType(ticketType),
            label: 'orders.status.Edited',
          },
        ];
      case OrderItemStatusEnumResponseDto.Paid:
        return [
          {
            textColor: 'var(--color-white)',
            color: 'var(--color-secondary-600)',
            icon: 'euro',
            label: 'orders.status.Paid',
          },
        ];
      case OrderItemStatusEnumResponseDto.Canceled:
        return [
          {
            textColor: 'var(--color-black)',
            color: 'var(--color-grey-100)',
            icon: 'close',
            label: 'orders.status.Canceled',
          },
        ];
      case OrderItemStatusEnumResponseDto.Reserved:
        return [
          {
            textColor: 'var(--color-white)',
            color: 'var(--color-grey-700)',
            icon: 'turned_in',
            label: 'orders.status.Reserved',
          },
        ];
      default:
        return [
          {
            textColor: 'var(--color-black)',
            color: 'var(--color-grey-100)',
            icon: 'info',
            label: `orders.status.${status}`,
          },
        ];
    }
  }

  private getPrintingIconByTicketType(ticketType: TicketTypeEnumResponseDto): string {
    switch (ticketType) {
      case TicketTypeEnumResponseDto.Pdf:
        return 'picture_as_pdf';
      case TicketTypeEnumResponseDto.Boca:
        return 'confirmation_number';
      default:
        return 'print';
    }
  }

  isPrintable(rowData: TreeListRowData): boolean {
    if (!rowData) return false;

    switch (rowData.orderItemRowType) {
      case OrderItemRowTypeEnumResponseDto.Product:
        return this.isProductPrintable(rowData);

      case OrderItemRowTypeEnumResponseDto.SubscriptionGroup:
        return (
          rowData.statusGroup === OrderItemStatusEnumResponseDto.Paid ||
          (rowData.statusGroup === OrderItemStatusEnumResponseDto.Edited &&
            this.getSubscriptionGroupNonCanceledTotalCount(rowData) > (rowData.statusCount ?? 0))
        );

      default:
        return rowData.statusGroup === OrderItemStatusEnumResponseDto.Paid;
    }
  }

  isPaid(selectedFolders: TreeListRowData[]): boolean {
    return all(selectedFolders, (selectedFolder) =>
      include([OrderStatusGroupEnum.Paid, OrderStatusGroupEnum.Edited], selectedFolder?.statusGroup),
    );
  }

  public isReserved(folders: TreeListRowData[]): boolean {
    return (
      folders?.length &&
      folders.every((folder) => folder?.statusGroup === OrderItemStatusEnumResponseDto.Reserved)
    );
  }

  isReservedFolders(folders: Array<GetOrderItemDetailsResponseDto | TreeListRowData>): boolean {
    const list = folders ?? [];
    if (!list.length) return false;
    return list.every((folder) => this.isReservedFolder(folder));
  }

  isReservedFolder(folder: GetOrderItemDetailsResponseDto | TreeListRowData | null | undefined): boolean {
    if (!folder) return false;
    if (this.isTreeListRow(folder)) {
      return folder.statusGroup === OrderItemStatusEnumResponseDto.Reserved;
    }
    return folder.statusGroup === OrderStatusGroupEnumResponseDto.Reserved;
  }

  private isTreeListRow(value: unknown): value is TreeListRowData {
    return !!value && (value as TreeListRowData).orderItemRowType != null;
  }
}
